<template>
  <header class="pb-4">
    <h2 class="text-lg">{{ t('cpms.access_management.assignments.title') }}</h2>
    <p class="text-xs text-neutral-500">{{ t('cpms.access_management.assignments.description') }}</p>

    <div class="mt-4 max-w-[48rem] w-full space-y-4">
      <div v-if="loading" class="bg-white border rounded-md p-3">
        <div class="flex items-center justify-center py-4">
          <InfuseSpinner size="small" />
        </div>
      </div>

      <InfuseNote v-else-if="assignedAccess.length === 0" class="py-2">
        {{ t('cpms.access_management.assignments.none') }}
      </InfuseNote>

      <template v-else>
        <!-- Externally Managed -->
        <div v-if="externallyManagedItems.length > 0" class="bg-white border rounded-md p-3">
          <h3 class="text-sm font-medium">{{ t('cpms.access_management.sections.externally_managed') }}</h3>
          <p class="text-xs text-neutral-500 mb-2">{{ t('cpms.access_management.externally_managed.description') }}</p>
          <div class="space-y-2">
            <div
              v-for="item in externallyManagedItems"
              :key="item.id"
              class="flex items-center justify-between p-3 border rounded bg-neutral-50"
            >
              <div class="flex items-center gap-3">
                <InfuseTooltip :content="t('cpms.access_management.externally_managed.tooltip')">
                  <font-awesome-icon :icon="['fal', 'cloud']" class="text-neutral-700" />
                </InfuseTooltip>
                <div class="text-sm font-medium">{{ item.name }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Direct Access -->
        <div v-if="directItems.length > 0" class="bg-white border rounded-md p-3">
          <h3 class="text-sm font-medium">{{ t('cpms.access_management.sections.direct') }}</h3>
          <p class="text-xs text-neutral-500 mb-2">{{ t('cpms.access_management.direct.description') }}</p>
          <div class="space-y-2">
            <div
              v-for="item in directItems"
              :key="item.id"
              class="flex items-center justify-between p-3 border rounded bg-neutral-50"
            >
              <div class="flex items-center gap-3">
                <font-awesome-icon
                  :icon="item.type === 'tenant' ? ['fal', 'buildings'] : ['fal', 'building']"
                  class="text-neutral-700"
                />
                <div class="text-sm font-medium">{{ item.name }}</div>
              </div>

              <InfuseSquareIconButton
                v-if="canUnassign"
                :border="false"
                :icon="['far', 'trash-can']"
                :tooltip-text="t('cpms.access_management.unassign.action.description')"
                :tooltip-title="t('cpms.access_management.unassign.action.label')"
                color="alter"
                size="sm"
                @click="handleUnassign(item.id)"
              />
            </div>
          </div>
        </div>

        <!-- Inherited (Indirect) -->
        <div v-if="inheritedItems.length > 0" class="bg-white border rounded-md p-3">
          <h3 class="text-sm font-medium">{{ t('cpms.access_management.sections.inherited') }}</h3>
          <p class="text-xs text-neutral-500 mb-2">{{ t('cpms.access_management.inherited.description') }}</p>
          <div class="space-y-2">
            <div
              v-for="item in inheritedItems"
              :key="item.id"
              class="flex items-center justify-between p-3 border rounded bg-neutral-50"
            >
              <div class="flex items-center gap-3">
                <InfuseTooltip :content="t('cpms.access_management.inherited.tooltip')">
                  <font-awesome-icon :icon="['fas', 'link']" class="text-neutral-700" />
                </InfuseTooltip>
                <div>
                  <div class="text-sm font-medium">{{ item.name }}</div>
                  <div class="text-xs text-neutral-500">
                    {{ item.inheritedFromType ? t('cpms.access_management.inherited.via', { type: t(`cpms.access_management.inherited.entity_type.${item.inheritedFromType}`, item.inheritedFromType) }) : t('cpms.access_management.inherited.via_unknown') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="flex gap-4 mt-3">
        <InfuseButton v-if="canAssign" :disabled="loading" color="primary" @click="openAssignModal">
          {{ t('cpms.access_management.assign.action.label') }}
        </InfuseButton>
      </div>
    </div>

    <InfuseModal
      v-if="showAssignModal"
      :title="t('cpms.access_management.assign.modal.title')"
      @close="closeAssignModal"
    >
      <InfuseField :label="t('cpms.access_management.assign.modal.destination')" size="small">
        <InfuseSearchSelect
          :loading="loading"
          :model-value="selectedDestination"
          :options="filteredDestinationOptions"
          placeholder="..."
          name="destination"
          :format="formatDestinationValue"
          @update:model-value="selectedDestination = $event"
        />
      </InfuseField>

      <template #actions>
        <InfuseButton :disabled="loading" color="secondary" @click="closeAssignModal">
          {{ t('action.cancel') }}
        </InfuseButton>
        <InfuseButton :loading="loading" color="primary" @click="confirmAssign">
          {{ t('action.select') }}
        </InfuseButton>
      </template>
    </InfuseModal>
  </header>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useNotify } from '@infuse/cpms/ui';
import { parseDestination, useDestinationOptions } from '@infuse/iam/composables';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { UserLevel } from '@infuse/iam/user/type';
import {
  InfuseButton,
  InfuseField,
  InfuseModal,
  InfuseNote,
  InfuseSearchSelect,
  InfuseSpinner,
  InfuseSquareIconButton,
  InfuseTooltip
} from '@infuse/ui/components';
import { getAxiosErrorMessage } from '@infuse/utils';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { AssignmentItem, AssignRequest } from './model/model';

const { t } = useI18n();
const { notifySuccess, notifyFailure } = useNotify();
const currentUserStore = useCurrentUserStore();

const canAssign = computed(() => {
  if (!props.assignPermissionId) {
    return true;
  }
  return !currentUserStore.enabled || currentUserStore.hasPermission(props.assignPermissionId);
});

const canUnassign = computed(() => {
  if (!props.unassignPermissionId) {
    return true;
  }
  return !currentUserStore.enabled || currentUserStore.hasPermission(props.unassignPermissionId);
});

interface Props {
  entityId: string;
  // eslint-disable-next-line no-unused-vars
  onAssign: (entityId: string, request: AssignRequest) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  onUnassign: (entityId: string, request: AssignRequest) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  onFetchOwners?: (entityId: string) => Promise<AssignmentItem[]>;
  assignPermissionId?: string;
  unassignPermissionId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  onFetchOwners: undefined,
  assignPermissionId: undefined,
  unassignPermissionId: undefined
});

const selectedDestination = ref<string | null>(null);
const loading = ref<boolean>(false);
const showAssignModal = ref<boolean>(false);
const assignedAccess = ref<AssignmentItem[]>([]);

// If the user is not at the domain level, exclude the domain and tenant option from the list of destinations
const {
  destinationOptions,
  fetchDestinations,
  formatDestinationValue
} = useDestinationOptions({ excludeDomain: true, excludeTenant: currentUserStore.level != UserLevel.DOMAIN });

// Watch selected destination for search queries
watch(selectedDestination, (val) => {
  if (
    !val ||
    val === 'domain' ||
    val.startsWith('tenant:') ||
    val.startsWith('domain-organisation:') ||
    val.startsWith('tenant-organisation:')
  ) {
    return;
  }
  fetchDestinations(val);
});

const externallyManagedItems = computed(() =>
  assignedAccess.value.filter((i) => i.externallyManaged)
);
const directItems = computed(() =>
  assignedAccess.value.filter((i) => !i.inherited && !i.externallyManaged)
);
const inheritedItems = computed(() =>
  assignedAccess.value.filter((i) => i.inherited && !i.externallyManaged)
);

const filteredDestinationOptions = computed(() => {
  return destinationOptions.value.filter((option) => {
    try {
      const parsed = parseDestination(option.value);

      if (!parsed.destinationId) {
        return false;
      }

      const isAlreadyAssigned = assignedAccess.value.some((assigned) => {
        return assigned.id === parsed.destinationId;
      });

      return !isAlreadyAssigned;
    } catch (err) {
      console.error('Error parsing destination:', err);
      return false;
    }
  });
});

async function fetchOwners() {
  if (props.onFetchOwners) {
    try {
      assignedAccess.value = await props.onFetchOwners(props.entityId);
    } catch (err) {
      console.error('Failed to fetch owners', err);
    }
  }
}

onMounted(async () => {
  showAssignModal.value = false;
  loading.value = true;
  try {
    await fetchOwners();
  } catch (err) {
    console.error('Failed to initialize', err);
  } finally {
    loading.value = false;
  }
});

function openAssignModal() {
  selectedDestination.value = null;
  showAssignModal.value = true;
  fetchDestinations('');
}

function closeAssignModal() {
  showAssignModal.value = false;
  selectedDestination.value = null;
}

async function handleUnassign(assignmentId: string) {
  // Find the assignment to get rootId and parentId
  const assignment = assignedAccess.value.find((item) => item.id === assignmentId);
  if (!assignment) {
    console.error('Assignment not found');
    return;
  }

  loading.value = true;
  try {
    const request: AssignRequest = {
      rootId: assignment.id
    };
    // Include parentId if it exists (for tenant-organisation assignments) - Only execute for domain-level user
    if (assignment.parentId && currentUserStore.level == UserLevel.DOMAIN) {
      request.parentId = assignment.parentId;
    }

    await props.onUnassign(props.entityId, request);
    await fetchOwners();

    notifySuccess({
      title: t('cpms.access_management.unassign.notify.success.title'),
      message: t('cpms.access_management.unassign.notify.success.message')
    });
  } catch (err) {
    notifyFailure({
      title: t('cpms.access_management.unassign.notify.error.title'),
      message: getAxiosErrorMessage(err, t('cpms.access_management.unassign.notify.error.message'))
    });
  } finally {
    loading.value = false;
  }
}

async function confirmAssign() {
  if (!selectedDestination.value) {
    return;
  }

  loading.value = true;
  try {
    const parsed = parseDestination(selectedDestination.value);

    if (!parsed.destinationId) {
      throw new Error('Invalid destination: destinationId is required');
    }

    const request: AssignRequest = {
      rootId: parsed.destinationId
    };

    // Only execute for domain-level user
    if (
      parsed.destinationType === UserLevel.ORGANISATION &&
      parsed.targetTenantId &&
      currentUserStore.level == UserLevel.DOMAIN
    ) {
      // Check if the tenant (parentId) is already assigned
      const tenantAlreadyAssigned = assignedAccess.value.some((item) => item.id === parsed.targetTenantId);
      if (!tenantAlreadyAssigned) {
        request.parentId = parsed.targetTenantId;
      }
    }

    await props.onAssign(props.entityId, request);

    await fetchOwners();

    closeAssignModal();

    notifySuccess({
      title: t('cpms.access_management.assign.notify.success.title'),
      message: t('cpms.access_management.assign.notify.success.message')
    });
  } catch (err) {
    notifyFailure({
      title: t('cpms.access_management.assign.notify.error.title'),
      message: getAxiosErrorMessage(err, t('cpms.access_management.assign.notify.error.message'))
    });
  } finally {
    loading.value = false;
  }
}
</script>
